import React from 'react';

import styled from '@emotion/styled';

import { Box, Fieldset, Stepper } from '@clutter/clean';
import { Accordion } from '@clutter/clean';

import { wt } from '@portal/initializers/wt';
import { PricingSetPriceEntryFragment, StoragePlanFragment } from '@portal/schema';

import { AccordionLabel } from '../accordion_label';
import {
  accordionMetadata,
  PLAN_UPDATE_PAGE_NAME,
  planSizeMetadata,
  storageChanged,
  storageTitle,
  storageValue,
} from '../util';
import { Option } from './option';

const DEFAULT_QUANTITY = 1;

const OptionsContainer = styled(Fieldset)`
  display: grid;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 0 24px 24px 24px;
`;

export const Selector: React.FC<{
  quantity: number;
  orderID: string;
  loading: boolean;
  initialPriceEntry?: PricingSetPriceEntryFragment;
  selectedPriceEntry?: PricingSetPriceEntryFragment;
  options: readonly PricingSetPriceEntryFragment[];
  onSelect(entry: PricingSetPriceEntryFragment): void;
  onQuantity(quantity: number): void;
}> = ({ orderID, loading, initialPriceEntry, selectedPriceEntry, quantity, options, onSelect, onQuantity }) => {
  const initialPricingEntryPlanBox = initialPriceEntry?.price.plan.name === 'Box';
  const currentPricingEntryPlanBox = selectedPriceEntry?.price.plan.name === 'Box';

  const sortedOptions = options
    .filter((option) => initialPricingEntryPlanBox || option.price.plan.name !== 'Box')
    .sort((a, b) => {
      if (a.price.plan.name === 'Box') return +1;
      if (b.price.plan.name === 'Box') return -1;
      return (a.price.plan as StoragePlanFragment).cuft - (b.price.plan as StoragePlanFragment).cuft;
    });

  const onToggle = (isOpen: boolean) => {
    const params = {
      ...accordionMetadata(isOpen),
      container: 'plan_size_accordion',
      order_id: orderID,
    };
    wt.track(params);
  };

  const onChange = (currentPriceEntry: PricingSetPriceEntryFragment, label: string) => {
    const params = {
      container: 'plan_size_accordion',
      previous_selection: planSizeMetadata(selectedPriceEntry),
      current_selection: planSizeMetadata(currentPriceEntry),
      action: 'click',
      label: label,
      pageName: PLAN_UPDATE_PAGE_NAME,
      objectType: 'input:button',
      objectName: 'plan_size_tile',
      order_id: orderID,
    };
    wt.track(params);
    if (currentPriceEntry.price.plan.name !== 'Box') onQuantity(DEFAULT_QUANTITY);
    onSelect(currentPriceEntry);
  };

  return (
    <Accordion.Item
      name="storage"
      label={
        <AccordionLabel
          loading={loading}
          title={storageTitle(selectedPriceEntry)}
          value={storageValue(selectedPriceEntry, quantity)}
          changed={storageChanged(selectedPriceEntry, initialPriceEntry)}
        />
      }
      onToggle={onToggle}
    >
      <OptionsContainer>
        {sortedOptions.map((option) => (
          <Option
            key={option.id}
            span={option.price.plan.name === 'Box' ? 3 : 1}
            option={option}
            selected={selectedPriceEntry?.price?.plan?.name === option.price.plan.name}
            onChange={onChange}
          />
        ))}
      </OptionsContainer>

      {currentPricingEntryPlanBox && (
        <Box.Flex padding="0 24px 24px 24px" justifyContent="center">
          <Stepper value={quantity ?? 0} onChange={onQuantity} min={1} max={999} disabled={false} />
        </Box.Flex>
      )}
    </Accordion.Item>
  );
};
